import {useEffect, useState} from 'react';
import {
  Autocomplete,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import {useLocation, useNavigate} from 'react-router-dom';

import LoadingIndicator from '../../components/loading-indicator';
import {AppContent, ContentRow} from '../../styled/components';
import TableHeader from '../../components/table-header';
import ConfirmDeletion from '../../components/confirm-deletion';
import useBEDatatable from '../../hooks/use-be-datatable';
import useUrlParam from '../../hooks/use-url-param';
import {deleteProvince, getCountriesForSelect, getProvinces} from './api-service';

function Provinces() {
  const navigate = useNavigate();
  const location = useLocation();
  // Both filters live in the URL (?s=...&countryId=...) so they survive
  // browser back/forward; the detail page also gets the list URL in the
  // router state so its "States list" button returns to the same view.
  const [countryId, setCountryId] = useUrlParam('countryId');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getCountriesForSelect()
      .then((result) => setCountries(result.data || []))
      .catch(() => setCountries([]));
  }, []);

  const selectedCountry = countries.find((c) => String(c.id) === countryId) || null;
  const listLocation = `${location.pathname}${location.search}`;
  const openProvince = (path) => navigate(path, {state: {from: listLocation}});

  const {
    ready,
    instancesList,
    filter,
    numberOfPages,
    page,
    rangeEnd,
    rangeStart,
    totalInstances,
    selectedInstance,
    showDeleteConfirm,

    setPage,
    filterChange,
    deleteInstance,
    setShowDeleteConfirm,
    handleDeleteButtonClicked,
  } = useBEDatatable({
    loadInstancesServiceAction: getProvinces,
    deleteInstanceServiceAction: deleteProvince,
    extraParams: countryId ? {countryId} : {},
  });

  return (
    <>
      {!ready && <LoadingIndicator/>}
      <AppContent>
        <TableHeader
          title="States"
          showAddButton
          handleAddButtonClicked={() => openProvince('/contacts/provinces/new')}
          filter={filter}
          filterChange={filterChange}
          filterChildren={(
            <Autocomplete
              size="small"
              sx={{minWidth: 260, marginRight: 3}}
              options={countries}
              value={selectedCountry}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(_ev, country) => setCountryId(country?.id ?? '')}
              renderInput={(params) => <TextField {...params} label="Country"/>}
            />
          )}
          firstItemIndex={rangeStart}
          lastItemIndex={rangeEnd}
          totalItems={totalInstances}
        />
        <ContentRow>
          {ready && (
            <TableContainer component={Paper}>
              <Table sx={{minWidth: 650}} aria-label="provinces table">
                <TableHead>
                  <TableRow>
                    <TableCell>State Name</TableCell>
                    <TableCell>State Code</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {instancesList.length > 0 && instancesList.map((province) => (
                    <TableRow
                      key={`province-${province.id}`}
                      sx={{'td': {paddingTop: 1, paddingBottom: 1}, '&:last-child td, &:last-child th': {border: 0}}}
                    >
                      <TableCell>{province.name}</TableCell>
                      <TableCell>{province.code || ''}</TableCell>
                      <TableCell>{province.countryName || ''}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="show province"
                          onClick={() => openProvince(`/contacts/provinces/${province.id}`)}
                        >
                          <VisibilityIcon fontSize="small"/>
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="delete province"
                          onClick={() => handleDeleteButtonClicked(province)}
                        >
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {instancesList.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={4}>
                        <Typography>There are no States yet. Add the first one by clicking the "Add new" button</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ContentRow>
        {numberOfPages > 1 && (
          <ContentRow style={{display: 'flex', justifyContent: 'center', columnGap: 16}}>
            <Pagination
              count={numberOfPages}
              page={page}
              variant="text"
              shape="rounded"
              color="primary"
              showFirstButton
              showLastButton
              onChange={(ev, p) => setPage(p)}
            />
          </ContentRow>
        )}
      </AppContent>
      {showDeleteConfirm && (
        <ConfirmDeletion
          handleClose={() => setShowDeleteConfirm(false)}
          handleConfirm={() => deleteInstance(selectedInstance)}
          deleteInstanceMsg={`state "${selectedInstance?.name || ''}"`}
        />
      )}
    </>
  );
}

export default Provinces;
